import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { SectionCard, EmptyState, Chip } from "../../components/Common";
import { Activity, AlertTriangle, HeartPulse, Plus, RefreshCw, ArrowRight } from "lucide-react";
import { RecordVitalsDialog } from "./RecordVitalsDialog";
import { vitalAlerts, hoursSince, humanAgo } from "./vitalsAlerts";
import { useNavigate } from "react-router-dom";

const DUE_HOURS = 4; // Overdue threshold

export default function NurseRounds() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordFor, setRecordFor] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState("all"); // all | overdue | alerts

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data: adms } = await api.get("/admissions?status=Admitted");
        const active = adms || [];
        const enriched = await Promise.all(active.map(async (a) => {
          try {
            const { data } = await api.get(`/vitals?patient_id=${encodeURIComponent(a.patient_id)}`);
            const list = data || [];
            const last = list[0] || null;
            return { adm: a, lastVitals: last };
          } catch {
            return { adm: a, lastVitals: null };
          }
        }));
        if (alive) setRows(enriched);
      } catch { toast.error("Failed to load rounds"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ha = a.lastVitals ? hoursSince(a.lastVitals.created_at) ?? 999 : 999;
      const hb = b.lastVitals ? hoursSince(b.lastVitals.created_at) ?? 999 : 999;
      return hb - ha;
    });
  }, [rows]);

  const overdue = sorted.filter((r) => {
    const h = r.lastVitals ? hoursSince(r.lastVitals.created_at) : null;
    return h == null || h > DUE_HOURS;
  });
  const withAlerts = sorted.filter((r) => vitalAlerts(r.lastVitals).hasAlert);

  const filtered = filter === "overdue" ? overdue : filter === "alerts" ? withAlerts : sorted;
  const toggle = (k) => setFilter((cur) => (cur === k ? "all" : k));

  return (
    <div className="space-y-4" data-testid="nurse-rounds">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Admitted patients"                 value={rows.length}       icon={HeartPulse}    tone="blue"  testId="rounds-kpi-total"    onClick={() => setFilter("all")}   active={filter === "all"} />
        <StatCard label={`Overdue (> ${DUE_HOURS}h)`}       value={overdue.length}    icon={AlertTriangle} tone="red"   testId="rounds-kpi-overdue"  onClick={() => toggle("overdue")}  active={filter === "overdue"} />
        <StatCard label="Abnormal last reading"             value={withAlerts.length} icon={Activity}      tone="amber" testId="rounds-kpi-alerts"   onClick={() => toggle("alerts")}   active={filter === "alerts"} />
      </div>

      <SectionCard
        title={filter === "overdue" ? `Ward vitals rounds · overdue > ${DUE_HOURS}h · ${overdue.length}`
              : filter === "alerts" ? `Ward vitals rounds · abnormal last reading · ${withAlerts.length}`
              : "Ward vitals rounds"}
        testId="rounds-list-card"
        actions={
          <div className="flex items-center gap-2">
            {filter !== "all" && (
              <button data-testid="rounds-clear-filter" onClick={() => setFilter("all")} className="h-8 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-medium">Show all</button>
            )}
            <button data-testid="rounds-refresh" onClick={() => setRefreshKey((k) => k + 1)} className="h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        }
      >
        {loading ? (
          <div className="p-6 text-sm text-slate-500" data-testid="rounds-loading">Loading rounds…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={HeartPulse} title={filter === "all" ? "No admitted patients" : "Nothing matches this filter"} description={filter === "all" ? "Vitals rounds appear here as soon as patients are admitted." : ""} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map(({ adm, lastVitals }) => {
              const hrs = lastVitals ? hoursSince(lastVitals.created_at) : null;
              const isOverdue = hrs == null || hrs > DUE_HOURS;
              const isDueSoon = hrs != null && hrs > 2 && hrs <= DUE_HOURS;
              const alerts = vitalAlerts(lastVitals);
              return (
                <li key={adm.admission_id} className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`round-row-${adm.admission_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{adm.patient_name}</span>
                      <span className="text-xs text-slate-500 tabular">{adm.patient_ths_id}</span>
                      <span className="text-xs text-slate-400">·</span>
                      <span className="text-xs text-slate-500">{adm.ward}{adm.bed_no ? ` · Bed ${adm.bed_no}` : ""}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {lastVitals ? (
                        <>
                          Last vitals {humanAgo(lastVitals.created_at)} by {lastVitals.recorded_by_name || "—"}
                        </>
                      ) : (
                        <span className="text-[#B71C1C]">No vitals recorded yet</span>
                      )}
                    </div>
                    {alerts.hasAlert && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`round-alerts-${adm.admission_id}`}>
                        {alerts.labels.map((l) => (
                          <span key={`${adm.admission_id}-${l}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#fdecec] text-[#B71C1C] font-medium">{l}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isOverdue && <Chip tone="red">Overdue</Chip>}
                    {isDueSoon && <Chip tone="amber">Due soon</Chip>}
                    <button
                      data-testid={`round-add-vitals-${adm.admission_id}`}
                      onClick={() => setRecordFor(adm)}
                      className="h-8 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] flex items-center gap-1.5"
                    ><Plus className="w-3.5 h-3.5" /> Add vitals</button>
                    <button
                      data-testid={`round-open-${adm.admission_id}`}
                      onClick={() => navigate(`/app/admitted`)}
                      title="Open in Admitted view"
                      className="h-8 px-2 rounded-lg text-xs text-slate-500 hover:bg-slate-100 flex items-center"
                    ><ArrowRight className="w-4 h-4" /></button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {recordFor && (
        <RecordVitalsDialog
          patientId={recordFor.patient_id}
          onClose={() => setRecordFor(null)}
          onSaved={() => { setRecordFor(null); setRefreshKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, testId, onClick, active }) {
  const toneCls = {
    blue: "text-[#1565C0] bg-blue-50",
    red:  "text-[#B71C1C] bg-[#fdecec]",
    amber:"text-[#B45309] bg-amber-50",
  }[tone] || "text-slate-600 bg-slate-100";
  const activeCls = active ? "ring-2 ring-[#1976D2] ring-offset-1 shadow-sm" : "hover:shadow-sm hover:border-slate-300";
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={`rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3 transition text-left w-full ${onClick ? "cursor-pointer" : ""} ${activeCls}`}
      data-testid={testId}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneCls}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-semibold text-slate-900 tabular">{value}</div>
      </div>
    </Comp>
  );
}
